import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    TextField,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    CircularProgress,
    TablePagination,
    Backdrop,
    Button,
    IconButton,
    Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTheme } from '@mui/material/styles';
import { DataTableWithDynamicSchemaProps } from './DataTable.types';
import { Logger } from "utils/Logger";
import { handleApiError } from 'utils/ErrorHandler';
import { apiClient } from "utils/ApiClient";
import { ColumnPreferenceManager, ColumnPreference } from 'utils/ColumnPreferenceManager';
import { ColumnSelector } from './ColumnSelector';

export const DataTableWithDynamicSchema = ({
    dataUrl,
    schemaUrl,
    columnsToExclude,
    onEdit,
    onDelete,
    columnRenderers,
    refreshTrigger = 0, // Default to 0, increment to trigger refresh
    onSchemaLoaded, // New prop to pass schema back to parent
    canEdit = true, // New prop to control edit permission
    canDelete = true, // New prop to control delete permission
    onView, // New prop for view functionality
    dataSetName, // New prop for dataset name to manage column preferences
}: DataTableWithDynamicSchemaProps & { 
    onSchemaLoaded?: (schema: any) => void;
    canEdit?: boolean;
    canDelete?: boolean;
    onView?: (record: any) => void;
    dataSetName?: string;
}) => {
    const theme = useTheme();
    const [rawData, setRawData] = useState<any>([]);
    const [schema, setSchema] = useState<any>(null); // Internal schema state
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [columnPreferences, setColumnPreferences] = useState<ColumnPreference>({
        mandatoryColumns: ['firstName', 'lastName', 'primaryMobile', 'primaryEmail'],
        optionalColumns: [],
        allColumns: ['firstName', 'lastName', 'primaryMobile', 'primaryEmail']
    });

    // State for sorting
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortOrder, setSortOrder] = useState('asc');

    // State for global search
    const [searchQuery, setSearchQuery] = useState('');

    // State for column-specific filters
    const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});

    // State for pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);



    // Helper function to extract endpoint path from full URL
    const getEndpointPath = (url: string): string => {
        try {
            // If it's already a relative path (starts with /), return as is
            if (url.startsWith('/')) {
                return url;
            }
            
            // If it's a full URL, extract the path part
            const urlObj = new URL(url);
            return urlObj.pathname;
        } catch (error) {
            // If URL parsing fails, assume it's already a relative path
            return url;
        }
    };

    // Effect to fetch schema only once when schemaUrl changes.
    // This runs independently of data fetching.
    useEffect(() => {
        const fetchSchema = async () => {
            try {
                // Set loading specifically for schema fetch
                setLoading(true); 
                // Extract endpoint path from full URL to avoid double URL construction
                const endpointPath = getEndpointPath(schemaUrl);
                Logger.debug("src/components/DataTable/DataTableWithDynamicSchema.tsx : Using endpoint path:", endpointPath);
                
                // Use ApiClient instead of fetch (automatically includes Authorization header)
                const schemaResult = await apiClient.get(endpointPath);
                setSchema(schemaResult?.data || null);
                Logger.debug("src/components/DataTable/DataTableWithDynamicSchema.tsx : Fetched schema from URL:", schemaResult?.data);
                
                // Load column preferences if dataset name is provided
                if (dataSetName && schemaResult?.data) {
                    const preferences = ColumnPreferenceManager.getColumnPreferences(dataSetName);
                    setColumnPreferences(preferences);
                    Logger.debug("DataTableWithDynamicSchema: Loaded column preferences:", preferences);
                }
                
                // Pass the fetched schema back to the parent, if a callback is provided
                if (onSchemaLoaded) {
                    onSchemaLoaded(schemaResult?.data || null);
                }
            } catch (err: any) {
                setError(`Failed to load schema: ${err.message}`);
                Logger.error("src/components/DataTable/DataTableWithDynamicSchema.tsx : fetchSchema: Failed to fetch schema:", err);
            } finally {
                // Ensure loading is set to false after schema fetch, regardless of success or failure
                setLoading(false); 
            }
        };

        fetchSchema();
    }, [schemaUrl, onSchemaLoaded, dataSetName]); // Re-fetch schema only if schemaUrl, onSchemaLoaded callback, or dataSetName changes

    // Effect to fetch data. This depends on schema being loaded and dataUrl/refreshTrigger.
    useEffect(() => {
        const fetchData = async () => {
            // Only proceed if schema is available to ensure data structure is known
            if (!schema) {
                Logger.debug("src/components/DataTable/DataTableWithDynamicSchema.tsx : fetchData: Schema not yet loaded, deferring data fetch.","");
                return;
            }
            try {
                // Set loading specifically for data fetch
                setLoading(true); 
                // Extract endpoint path from full URL to avoid double URL construction
                const endpointPath = getEndpointPath(dataUrl);
                Logger.debug("src/components/DataTable/DataTableWithDynamicSchema.tsx : Using data endpoint path:", endpointPath);
                
                // Use ApiClient instead of fetch (automatically includes Authorization header)
                const dataResult = await apiClient.get(endpointPath);
                setRawData(Array.isArray(dataResult.data) ? dataResult.data : []);
                Logger.debug("src/components/DataTable/DataTableWithDynamicSchema.tsx : Fetched data:", dataResult.data);
            } catch (err: any) {
                setError(`Failed to load data: ${err.message}`);
                Logger.error("src/components/DataTable/DataTableWithDynamicSchema.tsx : fetchData: Failed to fetch data:", err);
            } finally {
                // Ensure loading is set to false after data fetch, regardless of success or failure
                setLoading(false); 
            }
        };

        fetchData();
    }, [dataUrl, refreshTrigger, schema]); // Re-fetch data if dataUrl, refreshTrigger, or the schema object itself changes

    // Determine which columns to display based on schema and column preferences
    const displayColumns = useMemo(() => {
        if (!schema) return [];
        let allSchemaColumns: string[] = [];

        if (schema.paths) {
            allSchemaColumns = Object.keys(schema.paths);
        } else if (schema.obj) {
            allSchemaColumns = Object.keys(schema.obj);
        } else {
            // Fallback: if no specific schema structure, infer from rawData (less ideal for consistency)
            if (rawData.length > 0) {
                allSchemaColumns = Object.keys(rawData[0]);
            }
        }

        // Filter out excluded columns and system fields
        const availableColumns = allSchemaColumns.filter(key => {
            // Exclude the specific columns listed in columnsToExclude
            if (columnsToExclude.includes(key)) return false;
            
            // Exclude system fields that shouldn't be exposed in UI
            const systemFields = ['_id', 'createdAt', 'updatedAt', '__v'];
            if (systemFields.includes(key)) return false;
            
            return true;
        });

        // Use column preferences if dataset name is provided, otherwise show all columns
        if (dataSetName) {
            // Combine mandatory columns with selected optional columns
            const columnsToShow = [
                ...columnPreferences.mandatoryColumns,
                ...columnPreferences.optionalColumns
            ].filter(col => availableColumns.includes(col));
            
            // Always add 'actions' column
            return columnsToShow.concat('actions');
        } else {
            // Fallback: show all available columns
            return availableColumns.concat('actions');
        }
    }, [schema, rawData, columnsToExclude, columnRenderers, dataSetName, columnPreferences]);

    // Function to format column names for display
    const formatColumnName = (column: string) => {
        if (column === 'actions') return 'Actions';

        // Convert camelCase to "Title Case"
        return column
            .replace(/([A-Z])/g, ' $1') // Add space before capital letters
            .replace(/^./, str => str.toUpperCase()) // Capitalize the first letter
            .trim();
    };

    // Handle sorting logic
    const handleSort = (column: string) => {
        // Do not sort the 'actions' column
        if (column === 'actions') return;

        // Toggle sort order if clicking the same column, otherwise default to ascending
        if (sortColumn === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortOrder('asc');
        }
        setPage(0); // Reset to first page on new sort
    };

    // Handle global search input change
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        setPage(0); // Reset to first page on new search
    };

    // Handle individual column filter change
    const handleColumnFilterChange = (column: string, value: string) => {
        setColumnFilters(prevFilters => ({
            ...prevFilters,
            [column]: value,
        }));
        setPage(0); // Reset to first page on new filter
    };

    // Handle pagination page change
    const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    // Handle pagination rows per page change
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to first page when rows per page changes
    };

    // Memoized filtered and sorted data (computes only when dependencies change)
    const filteredAndSortedData = useMemo(() => {
        // Create a shallow copy to avoid mutating the original rawData array
        let currentData = [...rawData];

        // Helper to determine if a column is Boolean from schema
        const isBooleanColumn = (column: string): boolean => {
            return Boolean(schema?.paths?.[column]?.instance === 'Boolean');
        };

        // Helper to get display text for a cell (mirrors render logic)
        const getCellDisplayText = (row: any, column: string): string => {
            if (columnRenderers?.[column]) {
                return String(columnRenderers[column](row));
            }

            // Special handling for Consent_Records.browserInfo (keep short summary)
            if (dataSetName === 'Consent_Records' && column === 'browserInfo' && row[column]) {
                return row[column].userAgent ? `Browser: ${row[column].userAgent.split(' ')[0]}` : 'Unknown Browser';
            }

            // Date formatting (same as UI)
            if (schema.paths?.[column]?.instance === 'Date' && row[column]) {
                try {
                    return new Date(row[column]).toLocaleString('en-GB', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        timeZoneName: 'short'
                    });
                } catch (_e) {
                    return String(row[column] || '');
                }
            }

            // Boolean formatting (align with UI => "True"/"False")
            if (isBooleanColumn(column)) {
                return row[column] === true ? 'True' : row[column] === false ? 'False' : '';
            }

            return String(row[column] ?? '');
        };

        // Apply global search filter
        if (searchQuery) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            currentData = currentData.filter((row: any) =>
                displayColumns.some((column: string) => {
                    // Exclude 'actions' column from global search
                    if (column === 'actions') return false;

                    const valueToSearch = getCellDisplayText(row, column);
                    return valueToSearch.toLowerCase().includes(lowerCaseQuery);
                })
            );
        }

        // Apply column-specific filters
        Object.entries(columnFilters).forEach(([column, filterValue]) => {
            if (filterValue) {
                let lowerCaseFilterValue = filterValue.toLowerCase().trim();

                // Normalize boolean filter terms to match display text
                if (isBooleanColumn(column)) {
                    const truthy = ['true', 't', 'yes', 'y', '1'];
                    const falsy = ['false', 'f', 'no', 'n', '0'];
                    if (truthy.includes(lowerCaseFilterValue)) lowerCaseFilterValue = 'true';
                    if (falsy.includes(lowerCaseFilterValue)) lowerCaseFilterValue = 'false';
                }

                currentData = currentData.filter((row: any) => {
                    const valueToFilter = getCellDisplayText(row, column).toLowerCase();
                    return valueToFilter.includes(lowerCaseFilterValue);
                });
            }
        });

        // Apply sorting
        if (sortColumn && sortColumn !== 'actions') {
            currentData = currentData.sort((a: any, b: any) => {
                // Get comparison values, applying custom renderer if available
                const aValue = columnRenderers?.[sortColumn]
                    ? String(columnRenderers[sortColumn](a)).toLowerCase()
                    : String(a[sortColumn] || '').toLowerCase();
                const bValue = columnRenderers?.[sortColumn]
                    ? String(columnRenderers[sortColumn](b)).toLowerCase()
                    : String(b[sortColumn] || '').toLowerCase();

                // Perform string comparison based on sort order
                if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return currentData;
    }, [rawData, searchQuery, columnFilters, sortColumn, sortOrder, displayColumns, columnRenderers]);

    // Memoized data for the current page (slices the filtered and sorted data)
    const paginatedData = useMemo(() => {
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return filteredAndSortedData.slice(startIndex, endIndex);
    }, [filteredAndSortedData, page, rowsPerPage]);

    // Render loading state if either schema or data is being fetched
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '256px' }}>
                <Backdrop
                    sx={(theme) => ({ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 })}
                    open={loading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            </Box>
        );
    }

    // Render error message if an error occurred during fetching
    if (error) {
        return (
            <Box
                sx={{
                    color: 'red.600',
                    fontWeight: 'semibold',
                    padding: 2,
                    backgroundColor: 'red.50',
                    border: '1px solid',
                    borderColor: 'red.400',
                    borderRadius: '8px',
                }}
            >
                Error: {error}
            </Box>
        );
    }

    // Render message if no schema or displayable columns are found (after loading)
    if (!schema || displayColumns.length === 0) {
        return (
            <Box
                sx={{
                    color: 'gray.600',
                    padding: 2,
                    backgroundColor: 'warning.light',
                    border: '1px solid',
                    borderColor: 'warning.main',
                    borderRadius: '8px',
                }}
            >
                No schema or displayable columns found.
            </Box>
        );
    }

    // Main DataTable rendering
    return (
        <Box>
            {/* Column Selector and Search Row - Full Width Container */}
            <Box 
                sx={{ 
                    marginBottom: 3, 
                    width: '100%',
                    p: 2,
                    backgroundColor: 'background.paper',
                    borderRadius: 1,
                    boxShadow: 1,
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    flexWrap: 'wrap', 
                    gap: 2,
                    width: '100%'
                }}>
                    {/* Column Selector */}
                    {dataSetName && schema && (
                        <ColumnSelector
                            dataSetName={dataSetName}
                            allSchemaColumns={Object.keys(schema.paths || schema.obj || {})}
                            onColumnPreferencesChange={setColumnPreferences}
                            currentPreferences={columnPreferences}
                        />
                    )}
                    
                    {/* Global Search Input */}
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                        <TextField
                            fullWidth
                            label="Search all fields..."
                            variant="outlined"
                            size="small"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            sx={{
                                width: { xs: '100%', md: '50%' },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px',
                                },
                                transition: 'all 0.2s ease-in-out',
                                '& .MuiOutlinedInput-input': {
                                    padding: '12px 16px',
                                    fontSize: '0.875rem',
                                },
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    overflowX: 'auto',
                }}
            >
                <Table stickyHeader aria-label="Dynamic Data Table">
                    <TableHead>
                        <TableRow>
                            {displayColumns.map((column) => (
                                <TableCell
                                    key={column}
                                    onClick={() => handleSort(column)}
                                    sx={{
                                        cursor: (column === 'actions') ? 'default' : 'pointer', // No sorting cursor for actions
                                        userSelect: 'none', // Prevent text selection on header click
                                        '&:hover': {
                                            backgroundColor: (column === 'actions') ? 'inherit' : theme.palette.background.paper, // Hover effect for sortable columns
                                        },
                                        textTransform: 'uppercase', // Uppercase header text
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {formatColumnName(column)}
                                        {/* Show sort indicator if column is currently sorted */}
                                        {sortColumn === column && column !== 'actions' && (
                                            <Box component="span" sx={{ marginLeft: 1 }}>
                                                {sortOrder === 'asc' ? '▲' : '▼'}
                                            </Box>
                                        )}
                                    </Box>
                                </TableCell>
                            ))}
                        </TableRow>
                        {/* Column Filter Row */}
                        <TableRow>
                            {displayColumns.map((column) => (
                                <TableCell key={`filter-${column}`}>
                                    {(column !== 'actions') ? ( // Don't show filter for Actions column
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            placeholder={
                                                schema.paths?.[column]?.instance === 'Boolean'
                                                    ? `Filter ${formatColumnName(column)} (True/False)`
                                                    : `Filter ${formatColumnName(column)}`
                                            }
                                            value={columnFilters[column] || ''}
                                            onChange={(e) => handleColumnFilterChange(column, e.target.value)}
                                            onClick={(e) => e.stopPropagation()} // Prevent sort trigger when clicking filter input
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '4px',
                                                    fontSize: '0.875rem',
                                                },
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '8px 12px',
                                                },
                                            }}
                                        />
                                    ) : (
                                        <Box sx={{ height: '40px' }} /> // Placeholder to maintain column alignment
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedData.length > 0 ? (
                            paginatedData.map((row: any) => (
                                <TableRow key={row._id}>
                                    {displayColumns.map((column) => (
                                        <TableCell
                                            key={`${row._id}-${column}`}
                                            sx={{
                                                whiteSpace: 'nowrap', // Keep content on a single line
                                                fontSize: '0.875rem',
                                            }}
                                        >
                                            {column === 'actions' ? (
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    {canEdit && onEdit && (
                                                        <Tooltip title="Edit">
                                                            <Button
                                                                color="secondary"
                                                                variant="contained"
                                                                size="small"
                                                                onClick={() => onEdit!(row)}
                                                                sx={{
                                                                    borderRadius: 1,
                                                                    textTransform: 'none',
                                                                    px: 1.25,
                                                                    minWidth: 36,
                                                                }}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </Button>
                                                        </Tooltip>
                                                    )}
                                                    {canDelete && onDelete && (
                                                        <Tooltip title="Delete">
                                                            <Button
                                                                color="error"
                                                                variant="contained"
                                                                size="small"
                                                                onClick={() => onDelete!(row)}
                                                                sx={{
                                                                    borderRadius: 1,
                                                                    textTransform: 'none',
                                                                    px: 1.25,
                                                                    minWidth: 36,
                                                                }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </Button>
                                                        </Tooltip>
                                                    )}
                                                    {onView && (
                                                        <Tooltip title="View Details">
                                                            <Button
                                                                color="info"
                                                                variant="contained"
                                                                size="small"
                                                                onClick={() => onView!(row)}
                                                                sx={{
                                                                    borderRadius: 1,
                                                                    textTransform: 'none',
                                                                    px: 1.25,
                                                                    minWidth: 36,
                                                                }}
                                                            >
                                                                <VisibilityIcon fontSize="small" />
                                                            </Button>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            ) : (
                                                // Render cell content based on custom renderers or schema type
                                                columnRenderers?.[column] ? (
                                                    columnRenderers[column](row)
                                                ) : (
                                                    // Special handling for browserInfo object in Consent_Records dataset
                                                    dataSetName === 'Consent_Records' && column === 'browserInfo' && row[column] ? (
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                                {row[column].userAgent ? `Browser: ${row[column].userAgent.split(' ')[0]}` : 'Unknown Browser'}
                                                            </Typography>
                                                            {row[column].language && (
                                                                <Typography variant="caption" color="textSecondary">
                                                                    Language: {row[column].language}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        schema.paths?.[column]?.instance === 'Date' && row[column]
                                                            ? new Date(row[column]).toLocaleString('en-GB', {
                                                                year: 'numeric',
                                                                month: '2-digit',
                                                                day: '2-digit',
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                second: '2-digit',
                                                                timeZoneName: 'short'
                                                            })
                                                            : schema.paths?.[column]?.instance === 'Boolean'
                                                            ? (row[column] === true ? 'True' : row[column] === false ? 'False' : '')
                                                            : String(row[column] || '')
                                                    )
                                                )
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={displayColumns.length}
                                    sx={{
                                        textAlign: 'center',
                                        padding: 2,
                                    }}
                                >
                                    No matching records found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Table Pagination */}
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]} // Options for rows per page dropdown
                component="div" // Render as a div element
                count={filteredAndSortedData.length} // Total number of items after filtering and sorting
                rowsPerPage={rowsPerPage} // Number of rows to display per page
                page={page} // Current page (0-indexed)
                onPageChange={handleChangePage} // Callback for page change
                onRowsPerPageChange={handleChangeRowsPerPage} // Callback for rows per page change
                sx={{
                    '.MuiTablePagination-toolbar': {
                        flexWrap: 'wrap', // Allow toolbar items to wrap on smaller screens
                        justifyContent: 'center', // Center content horizontally
                    },
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                        marginBottom: { xs: 1, sm: 0 }, // Add margin on small screens for better spacing
                    },
                }}
            />
        </Box>
    );
};
